import type { 
  TweetReply, 
  BotAccount, 
  TokenShillActivity, 
  PumpDumpIndicator,
  ContentQualityMetrics 
} from '@podx/core/types';
import { config } from '@podx/core/config';
import { logger } from '@podx/core/logger';

/**
 * BotTracker - Tracks bot accounts and their token shilling activities
 * 
 * This class maintains a persistent database of bot accounts and analyzes
 * their token promotion patterns to detect pump/dump schemes
 */
export class BotTracker {
  private botDatabase = new Map<string, BotAccount>();
  private tokenShillHistory = new Map<string, TokenShillActivity>();

  // Patterns that indicate shilling behavior
  private shillPatterns = [
    // Direct promotional language
    /(?:buy|get|grab|scoop)\s+\$([A-Z]{2,10})\s+(?:now|quick|fast|before|asap)/i,
    /\$([A-Z]{2,10})\s+(?:to|going|will\s+hit|target|moon|100x|1000x)/i,
    /(?:don\'?t\s+miss|last\s+chance|act\s+fast).*\$([A-Z]{2,10})/i,
    
    // Urgency and FOMO tactics
    /(?:only|just)\s+\d+\s+(?:hours?|minutes?|days?)\s+left.*\$([A-Z]{2,10})/i,
    /(?:limited|exclusive|insider).*(?:opportunity|access|info).*\$([A-Z]{2,10})/i,
    /\$([A-Z]{2,10}).*(?:pumping|mooning|exploding|skyrocketing)\s+(?:now|soon)/i,
    
    // Price predictions and guarantees
    /\$([A-Z]{2,10}).*(?:guaranteed|sure|certain).*(?:\d+x|\d+%|moon)/i,
    /(?:next|coming)\s+(?:100x|1000x|gem).*\$([A-Z]{2,10})/i,
    /\$([A-Z]{2,10})\s+(?:easy|guaranteed|sure)\s+(?:\d+x|\d+%)/i,
    
    // Coordinated messaging
    /(?:team|group|we)\s+(?:recommend|suggest|backing)\s+\$([A-Z]{2,10})/i,
    /(?:join|follow)\s+(?:us|our\s+team).*\$([A-Z]{2,10})/i,
    /(?:signal|call|alert).*\$([A-Z]{2,10})/i,
  ];

  // Exit/dump warning patterns
  private dumpPatterns = [
    /(?:sell|exit|dump|take\s+profits?).*\$([A-Z]{2,10})/i,
    /\$([A-Z]{2,10}).*(?:peaked|top|sell\s+zone|resistance)/i,
    /(?:warning|danger|risk).*\$([A-Z]{2,10})/i,
    /\$([A-Z]{2,10}).*(?:rug|scam|avoid|stay\s+away)/i,
  ];

  trackBotAccount(
    username: string, 
    replies: TweetReply[], 
    qualityMetrics: ContentQualityMetrics[],
    botScore: number,
    shillScore: number
  ): BotAccount {
    const now = new Date();
    let botAccount = this.botDatabase.get(username);

    if (!botAccount) {
      // Create new bot account record
      botAccount = {
        username,
        firstDetected: now,
        lastSeen: now,
        botScore,
        shillScore,
        reputation: this.determineReputation(botScore, shillScore),
        tokensShilled: [],
        behaviorPatterns: [],
        networksAssociated: [],
        totalReplies: 0,
        flaggedReplies: 0,
        accountMetadata: {
          profileCompleteness: this.calculateProfileCompleteness(username),
          usernamePattern: this.analyzeUsernamePattern(username)
        }
      };

      logger.info('New bot account detected', {
        operation: 'track_bot_account',
        username,
        botScore,
        shillScore,
        reputation: botAccount.reputation
      });
    } else {
      // Update existing bot account
      botAccount.lastSeen = now;
      botAccount.botScore = Math.max(botAccount.botScore, botScore);
      botAccount.shillScore = Math.max(botAccount.shillScore, shillScore);
      botAccount.reputation = this.determineReputation(botAccount.botScore, botAccount.shillScore);
    }

    // Update activity stats
    botAccount.totalReplies += replies.length;
    botAccount.flaggedReplies += qualityMetrics.filter(m => 
      m.spamProbability > config.botDetection.spamProbabilityThreshold
    ).length;

    // Analyze token shilling activity
    const shillActivity = this.analyzeTokenShilling(username, replies);
    this.updateTokenShillingHistory(botAccount, shillActivity);

    // Update behavior patterns
    this.updateBehaviorPatterns(botAccount, replies, qualityMetrics);

    this.botDatabase.set(username, botAccount);
    return botAccount;
  }

  private determineReputation(botScore: number, shillScore: number): BotAccount['reputation'] {
    if (botScore > 0.8 && shillScore > 0.7) {
      return 'shill_farm';
    } else if (botScore > 0.7) {
      return 'bot';
    } else if (shillScore > 0.6 || botScore > 0.4) {
      return 'suspicious';
    } else {
      return 'coordinated';
    }
  }

  private calculateProfileCompleteness(username: string): number {
    // Simple heuristic - would need actual profile data for accurate calculation
    let completeness = 0.5; // Base score
    
    // Username patterns that suggest incomplete profiles
    if (/^\w+\d{4,}$/.test(username)) completeness -= 0.2; // username1234 pattern
    if (/_\d+$/.test(username)) completeness -= 0.2; // username_123 pattern
    if (/^[a-z]+[A-Z]+\d+$/.test(username)) completeness -= 0.3; // randomString123 pattern
    
    return Math.max(0, Math.min(1, completeness));
  }

  private analyzeUsernamePattern(username: string): BotAccount['accountMetadata']['usernamePattern'] {
    // Generated patterns (bot-like)
    if (
      /^\w+\d{4,}$/.test(username) || // username1234
      /^\w+_\d{4,}$/.test(username) || // username_1234
      /^[a-z]+[A-Z]+\d+$/.test(username) || // randomString123
      /^\w{1,3}\d{8,}$/.test(username) // ab12345678
    ) {
      return 'generated';
    }
    
    // Suspicious patterns
    if (
      username.length > 15 ||
      (username.match(/\d/g) || []).length > 3 ||
      (username.match(/_/g) || []).length > 2
    ) {
      return 'suspicious';
    }
    
    return 'human_like';
  }

  private analyzeTokenShilling(username: string, replies: TweetReply[]): Map<string, TokenShillActivity> {
    const shillActivity = new Map<string, TokenShillActivity>();
    
    for (const reply of replies) {
      const tokens = this.extractTokensFromText(reply.text);
      const shillType = this.determineShillType(reply.text);
      
      for (const token of tokens) {
        if (!shillActivity.has(token)) {
          shillActivity.set(token, {
            tokenSymbol: token,
            contractAddress: this.extractContractAddress(reply.text),
            firstMentioned: new Date(reply.createdAt),
            lastMentioned: new Date(reply.createdAt),
            mentionCount: 0,
            promotionType: shillType,
            sentimentTrend: [],
            associatedAccounts: [username],
            pumpDumpIndicators: [],
            credibilityScore: 0
          });
        }
        
        const activity = shillActivity.get(token)!;
        activity.mentionCount++;
        activity.lastMentioned = new Date(reply.createdAt);
        
        // Add sentiment data point
        activity.sentimentTrend.push({
          timestamp: new Date(reply.createdAt),
          sentiment: reply.sentiment || 'neutral',
          intensity: Math.abs(reply.sentimentScore || 0)
        });
        
        // Analyze for pump/dump indicators
        const indicators = this.detectPumpDumpIndicators(reply.text, token, username);
        activity.pumpDumpIndicators.push(...indicators);
        
        // Update credibility score
        activity.credibilityScore = this.calculateCredibilityScore(reply, activity);
      }
    }
    
    return shillActivity;
  }

  private extractTokensFromText(text: string): string[] {
    const tokens: string[] = [];
    const tokenPattern = /\$([A-Z]{2,10})/g;
    let match;
    
    while ((match = tokenPattern.exec(text)) !== null) {
      const token = match[1];
      // Filter out common false positives
      if (!['USD', 'BTC', 'ETH'].includes(token)) {
        tokens.push(token);
      }
    }
    
    return [...new Set(tokens)]; // Remove duplicates
  }

  private extractContractAddress(text: string): string | undefined {
    // Ethereum contract pattern
    const ethPattern = /0x[a-fA-F0-9]{40}/;
    const ethMatch = text.match(ethPattern);
    if (ethMatch) return ethMatch[0];
    
    // Solana contract pattern  
    const solPattern = /[1-9A-HJ-NP-Za-km-z]{32,44}/;
    const solMatch = text.match(solPattern);
    if (solMatch) return solMatch[0];
    
    return undefined;
  }

  private determineShillType(text: string): TokenShillActivity['promotionType'] {
    const lowerText = text.toLowerCase();
    
    // Check for dump warnings first
    for (const pattern of this.dumpPatterns) {
      if (pattern.test(text)) {
        return 'dump_warning';
      }
    }
    
    // Check for pump campaign indicators
    for (const pattern of this.shillPatterns) {
      if (pattern.test(text)) {
        return 'pump_campaign';
      }
    }
    
    // Check for coordinated indicators
    if (
      /(?:team|group|signal|call|alert)/i.test(lowerText) ||
      /(?:join|follow).*(?:telegram|discord)/i.test(lowerText) ||
      /(?:vip|premium|exclusive).*(?:group|channel)/i.test(lowerText)
    ) {
      return 'coordinated';
    }
    
    return 'organic';
  }

  private detectPumpDumpIndicators(text: string, token: string, username: string): PumpDumpIndicator[] {
    const indicators: PumpDumpIndicator[] = [];
    const now = new Date();
    
    // Fake urgency detection
    const urgencyPatterns = [
      /(?:now|asap|quick|fast|hurry|urgent)/i,
      /(?:last\s+chance|limited\s+time|ending\s+soon)/i,
      /(?:only|just)\s+\d+\s+(?:hours?|minutes?|days?)\s+left/i
    ];
    
    for (const pattern of urgencyPatterns) {
      if (pattern.test(text)) {
        indicators.push({
          type: 'fake_urgency',
          confidence: 0.7,
          evidence: [`Urgency language detected: "${text.match(pattern)?.[0]}"`],
          timestamp: now,
          accountsInvolved: [username]
        });
        break;
      }
    }
    
    // Price manipulation language
    const priceManipulationPatterns = [
      /(?:guaranteed|sure|certain|easy).*(?:\d+x|\d+%|moon)/i,
      /(?:100x|1000x|10000x)/i,
      /(?:next\s+(?:bitcoin|eth|solana))/i
    ];
    
    for (const pattern of priceManipulationPatterns) {
      if (pattern.test(text)) {
        indicators.push({
          type: 'price_manipulation',
          confidence: 0.8,
          evidence: [`Price manipulation language: "${text.match(pattern)?.[0]}"`],
          timestamp: now,
          accountsInvolved: [username]
        });
        break;
      }
    }
    
    // Coordinated timing indicators
    if (text.includes('signal') || text.includes('call') || text.includes('alert')) {
      indicators.push({
        type: 'coordinated_timing',
        confidence: 0.6,
        evidence: ['Coordinated messaging language detected'],
        timestamp: now,
        accountsInvolved: [username]
      });
    }
    
    // Exit signal detection
    const exitPatterns = [
      /(?:sell|exit|take\s+profits?|dump)/i,
      /(?:peaked|top|resistance|sell\s+zone)/i,
      /(?:warning|danger|avoid)/i
    ];
    
    for (const pattern of exitPatterns) {
      if (pattern.test(text)) {
        indicators.push({
          type: 'exit_signals',
          confidence: 0.9,
          evidence: [`Exit signal detected: "${text.match(pattern)?.[0]}"`],
          timestamp: now,
          accountsInvolved: [username]
        });
        break;
      }
    }
    
    return indicators;
  }

  private calculateCredibilityScore(reply: TweetReply, activity: TokenShillActivity): number {
    let credibility = 0.5; // Base score
    
    // Positive credibility factors
    if (reply.text.length > 100) credibility += 0.1; // Longer, more detailed posts
    if (/https?:\/\/[^\s]+/.test(reply.text)) credibility += 0.1; // Contains links
    if (/(?:dyor|do\s+your\s+own\s+research|not\s+financial\s+advice)/i.test(reply.text)) credibility += 0.2;
    
    // Negative credibility factors
    if (activity.promotionType === 'pump_campaign') credibility -= 0.3;
    if (activity.pumpDumpIndicators.length > 0) credibility -= 0.2;
    if (reply.text.match(/(?:\!){2,}/)) credibility -= 0.1; // Multiple exclamation marks
    
    return Math.max(0, Math.min(1, credibility));
  }

  private updateTokenShillingHistory(botAccount: BotAccount, newActivity: Map<string, TokenShillActivity>): void {
    for (const [token, activity] of newActivity) {
      const existingIndex = botAccount.tokensShilled.findIndex(t => t.tokenSymbol === token);
      
      if (existingIndex >= 0) {
        // Merge with existing activity
        const existing = botAccount.tokensShilled[existingIndex];
        existing.lastMentioned = activity.lastMentioned;
        existing.mentionCount += activity.mentionCount;
        existing.sentimentTrend.push(...activity.sentimentTrend);
        existing.pumpDumpIndicators.push(...activity.pumpDumpIndicators);
        existing.credibilityScore = (existing.credibilityScore + activity.credibilityScore) / 2;
      } else {
        // Add new token activity
        botAccount.tokensShilled.push(activity);
      }
    }
  }

  private updateBehaviorPatterns(
    botAccount: BotAccount, 
    replies: TweetReply[], 
    qualityMetrics: ContentQualityMetrics[]
  ): void {
    const patterns: string[] = [];
    
    // Generic content pattern
    const genericRatio = qualityMetrics.filter(m => 
      m.genericityScore > config.botDetection.genericityThreshold
    ).length / Math.max(qualityMetrics.length, 1);
    
    if (genericRatio > 0.7) {
      patterns.push('high_generic_content');
    }
    
    // Manipulation pattern
    const manipulationRatio = qualityMetrics.filter(m => m.manipulationScore > 0.5).length / Math.max(qualityMetrics.length, 1);
    if (manipulationRatio > 0.3) {
      patterns.push('frequent_manipulation');
    }
    
    // Token shilling pattern
    const tokenMentions = replies.filter(r => /\$[A-Z]{2,10}/.test(r.text)).length;
    if (tokenMentions > replies.length * 0.5) {
      patterns.push('excessive_token_shilling');
    }
    
    // Update behavior patterns (keep unique)
    botAccount.behaviorPatterns = [...new Set([...botAccount.behaviorPatterns, ...patterns])];
  }

  // Public methods for accessing bot data
  
  getBotAccount(username: string): BotAccount | undefined {
    return this.botDatabase.get(username);
  }

  getAllBotAccounts(): BotAccount[] {
    return Array.from(this.botDatabase.values());
  }

  getBotAccountsByReputation(reputation: BotAccount['reputation']): BotAccount[] {
    return Array.from(this.botDatabase.values())
      .filter(bot => bot.reputation === reputation);
  }

  getTokenShillers(tokenSymbol: string): BotAccount[] {
    return Array.from(this.botDatabase.values())
      .filter(bot => bot.tokensShilled.some(t => t.tokenSymbol === tokenSymbol));
  }

  getMostActiveShillers(limit: number = 10): BotAccount[] {
    return Array.from(this.botDatabase.values())
      .sort((a, b) => b.tokensShilled.length - a.tokensShilled.length)
      .slice(0, limit);
  }

  getRecentlyDetectedBots(hours: number = 24): BotAccount[] {
    const cutoff = new Date(Date.now() - hours * 60 * 60 * 1000);
    return Array.from(this.botDatabase.values())
      .filter(bot => bot.firstDetected > cutoff);
  }

  // Statistics and analytics
  
  getBotStatistics(): {
    totalBots: number;
    activeInLast24h: number;
    byReputation: Record<BotAccount['reputation'], number>;
    averageTokensPerBot: number;
    mostShilledTokens: Array<{token: string; count: number}>;
  } {
    const allBots = Array.from(this.botDatabase.values());
    const recentCutoff = new Date(Date.now() - 24 * 60 * 60 * 1000);
    
    const byReputation: Record<BotAccount['reputation'], number> = {
      bot: 0,
      suspicious: 0,
      shill_farm: 0,
      coordinated: 0
    };
    
    const tokenCounts = new Map<string, number>();
    let totalTokens = 0;
    
    for (const bot of allBots) {
      byReputation[bot.reputation]++;
      totalTokens += bot.tokensShilled.length;
      
      for (const tokenActivity of bot.tokensShilled) {
        const count = tokenCounts.get(tokenActivity.tokenSymbol) || 0;
        tokenCounts.set(tokenActivity.tokenSymbol, count + 1);
      }
    }
    
    const mostShilledTokens = Array.from(tokenCounts.entries())
      .map(([token, count]) => ({token, count}))
      .sort((a, b) => b.count - a.count)
      .slice(0, 10);
    
    return {
      totalBots: allBots.length,
      activeInLast24h: allBots.filter(bot => bot.lastSeen > recentCutoff).length,
      byReputation,
      averageTokensPerBot: Math.round((totalTokens / Math.max(allBots.length, 1)) * 100) / 100,
      mostShilledTokens
    };
  }

  // Cleanup methods
  
  purgeOldBotData(daysOld: number = 30): number {
    const cutoff = new Date(Date.now() - daysOld * 24 * 60 * 60 * 1000);
    let removedCount = 0;
    
    for (const [username, bot] of this.botDatabase) {
      if (bot.lastSeen < cutoff) {
        this.botDatabase.delete(username);
        removedCount++;
      }
    }
    
    logger.info('Purged old bot data', {
      operation: 'purge_bot_data',
      removedCount,
      daysOld
    });
    
    return removedCount;
  }
}